
import { useState, useRef, useEffect } from 'react';

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cartCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [scrolled, setScrolled] = useState(false);

  const popularSearches = ['Magnezyum', 'Probiyotik', 'Sitikolin', 'Omega-3', 'Bebek'];

  // Track scroll to add shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the input when the search bar opens
  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  // Close mobile menu on larger screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // You can add actual search logic here.
    console.log('Search submitted:', { selectedCategory, searchQuery });
  };

  const handlePopularClick = (term: string) => {
    setSearchQuery(term);
    setSearchOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <header className={`bg-white sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-md' : 'shadow-sm'}`}>
      <div className="max-w-[1440px] mx-auto px-3 sm:px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-3 sm:gap-6">
          {/* Logo */}
          <a href="/" className="flex-shrink-0 cursor-pointer">
            <img
              src="https://vitassoilac.com//storage/settings/June2025/Yzv1HIhTpIKYsx25oNJk.png"
              alt="Vitasso - Organik Takviye Ürünleri"
              className="h-9 sm:h-12 w-auto"
            />
          </a>

          {/* Navigation */}
          <nav className="hidden lg:flex items-center gap-1 flex-shrink-0">
            <a href="/" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg">
              Ana Sayfa
            </a>
            <a href="#" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg flex items-center gap-1">
              Ürünler
            </a>
            <a href="#" className="text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-xs"></i>
              </div>
              Yeni Ürünler
            </a>
            <a href="#" className="text-amber-600 hover:bg-amber-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-discount-percent-line text-xs"></i>
              </div>
              Fırsatlar
            </a>
            <a href="#" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg">
              Hakkımızda
            </a>
            <a href="#" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2 rounded-lg">
              İletişim
            </a>
          </nav>

          {/* Search Bar (desktop) */}
          <div ref={searchRef} className="hidden md:block flex-1 max-w-md relative">
            <form onSubmit={handleSearch} className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-gray-50 hover:border-brand-300 focus-within:border-brand-400 focus-within:bg-white transition-all">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="h-10 px-3 text-xs text-gray-600 bg-transparent border-r border-gray-200 outline-none cursor-pointer whitespace-nowrap"
              >
                <option value="all">Tümü</option>
                <option value="magnesium">Magnezyum</option>
                <option value="baby">Bebek</option>
                <option value="omega">Omega & DHA</option>
                <option value="vitamin">Vitamin</option>
                <option value="care">Bakım</option>
              </select>
              <input
                ref={inputRef}
                type="text"
                placeholder="Size uygun ürünleri arayın..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                className="flex-1 h-10 px-3 text-sm text-gray-700 bg-transparent outline-none placeholder:text-gray-400"
              />
              <button
                type="submit"
                className="h-10 px-4 bg-brand-600 hover:bg-brand-700 text-white transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-base"></i>
                </div>
              </button>
            </form>

            {/* Popular searches dropdown */}
            {searchOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 p-4 z-50">
                <p className="text-xs text-gray-500 font-medium mb-2">Popüler Aramalar</p>
                <div className="flex flex-wrap gap-2">
                  {popularSearches.map((term) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => handlePopularClick(term)}
                      className="px-3 py-1.5 bg-gray-50 hover:bg-brand-50 hover:text-brand-600 rounded-full text-xs text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Mobile search toggle */}
            <button
              className="md:hidden flex items-center text-gray-700 hover:text-brand-600 transition-colors cursor-pointer whitespace-nowrap"
              onClick={() => setSearchOpen(!searchOpen)}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-search-line text-xl"></i>
              </div>
            </button>

            <a href="#" className="hidden sm:flex flex-col items-center text-gray-600 hover:text-brand-600 transition-colors cursor-pointer group">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-user-line text-xl"></i>
              </div>
              <span className="text-[10px] mt-0.5 hidden lg:block group-hover:text-brand-600">Hesabım</span>
            </a>

            <a href="#" className="hidden sm:flex flex-col items-center text-gray-600 hover:text-brand-600 transition-colors cursor-pointer group">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-heart-line text-xl"></i>
              </div>
              <span className="text-[10px] mt-0.5 hidden lg:block group-hover:text-brand-600">Favoriler</span>
            </a>

            <a href="#" className="flex flex-col items-center text-gray-600 hover:text-brand-600 transition-colors relative cursor-pointer group">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-cart-line text-xl"></i>
              </div>
              <span className="text-[10px] mt-0.5 hidden lg:block group-hover:text-brand-600">Sepetim</span>
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </a>

            {/* Mobile menu toggle */}
            <button
              className="lg:hidden text-gray-700 cursor-pointer whitespace-nowrap ml-1"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Search (visible when searchOpen is true) */}
        {searchOpen && (
          <div className="md:hidden mt-3 pb-1">
            <form onSubmit={handleSearch} className="flex items-center border border-gray-200 rounded-xl overflow-hidden bg-gray-50 focus-within:border-brand-400 focus-within:bg-white transition-all">
              <input
                type="text"
                placeholder="Ürün ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 h-10 px-3 text-sm text-gray-700 bg-transparent outline-none placeholder:text-gray-400"
              />
              <button type="submit" className="h-10 px-4 bg-brand-600 text-white cursor-pointer whitespace-nowrap flex items-center justify-center">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-base"></i>
                </div>
              </button>
            </form>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {popularSearches.map((term) => (
                <button
                  key={term}
                  type="button"
                  onClick={() => handlePopularClick(term)}
                  className="px-2.5 py-1 bg-gray-100 hover:bg-brand-50 hover:text-brand-600 rounded-full text-[11px] text-gray-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="lg:hidden mt-3 pb-3 border-t pt-3">
            <div className="flex flex-col gap-1">
              <a href="/" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2.5 rounded-lg">
                Ana Sayfa
              </a>
              <a href="#" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2.5 rounded-lg">
                Ürünler
              </a>
              <a href="#" className="text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2.5 rounded-lg flex items-center gap-2">
                <i className="ri-price-tag-3-line text-xs"></i> Yeni Ürünler
              </a>
              <a href="#" className="text-amber-600 hover:bg-amber-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2.5 rounded-lg flex items-center gap-2">
                <i className="ri-discount-percent-line text-xs"></i> Fırsatlar
              </a>
              <a href="#" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2.5 rounded-lg">
                Hakkımızda
              </a>
              <a href="#" className="text-gray-700 hover:text-brand-600 hover:bg-brand-50 transition-all font-medium text-sm cursor-pointer whitespace-nowrap px-3 py-2.5 rounded-lg">
                İletişim
              </a>
              {/* Mobile‑only quick links */}
              <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-100 px-3">
                <a href="#" className="flex items-center gap-2 text-gray-600 text-sm cursor-pointer">
                  <i className="ri-user-line text-base"></i> Hesabım
                </a>
                <a href="#" className="flex items-center gap-2 text-gray-600 text-sm cursor-pointer">
                  <i className="ri-heart-line text-base"></i> Favoriler
                </a>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
